import Axios from 'axios';
import { saveShopData, getShopData } from '../utilities/shopStorage';

/* Config */
const accessKey = window.shopwareConfig?.accessKey;

/**
 * Cart service functions automatically dispatch 'cart:updated' events
 * when cart operations are successful. This allows other components
 * (like the cart icon in the header) to automatically update their
 * display without manual event dispatching.
 */

/**
 * Creates a new cart and stores the context token
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<Object>} Cart data
 */
async function createNewCart({shopwareUrl}) {
    const url = `${shopwareUrl}/checkout/cart`;

    try {
        const response = await Axios.post(url, {}, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
            }
        });

        // Store the new context token from response headers in localStorage
        const newToken = response.headers['sw-context-token'];

        if (newToken) {
            saveShopData('sw-context-token', newToken);
        }

        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * Ensures a valid context token exists, creates new cart if needed
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<string|null>} Context token
 */
export async function ensureValidContextToken({shopwareUrl}) {
    const existingToken = getShopData('sw-context-token');

    if (!existingToken) {
        // Generate new context token by creating a cart
        await createNewCart({shopwareUrl});
    }

    return getShopData('sw-context-token');
}

/**
 * Handles cart-related errors and attempts recovery
 * @param {Error} error - The error object
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<boolean>} Whether retry is possible
 */
async function handleCartError(error, shopwareUrl) {
    if (error.response?.status === 403 || error.response?.status === 401 || error.response?.status === 404) {
        // Context token is invalid or cart doesn't exist, create new cart
        try {
            await createNewCart({shopwareUrl});
            return true; // Indicate retry is possible
        } catch (createError) {
            return false; // Cart creation failed
        }
    }
    return false; // No recovery possible
}

/**
 * Fetches existing cart or creates a new one following Shopware's "fetch or create" pattern
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<Object>} Cart data
 */
export async function fetchOrCreateCart({shopwareUrl}) {
    const url = `${shopwareUrl}/checkout/cart`;

    try {
        // First, try to fetch existing cart
        const response = await Axios.get(url, {
            headers: {
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-context-token': getShopData('sw-context-token') || ''
            }
        });
        return response.data;
    } catch (error) {
        // If fetch fails, try to recover by creating a new cart
        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            // Retry the fetch with new context token
            const response = await Axios.get(url, {
                headers: {
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-context-token': getShopData('sw-context-token') || ''
                }
            });
            return response.data;
        }
        throw error;
    }
}

/**
 * Gets cart items using the improved fetch or create pattern
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<Object>} Cart data
 */
export async function getCartItems({shopwareUrl}) {
    try {
        return await fetchOrCreateCart({shopwareUrl});
    } catch (error) {
        throw error;
    }
}

/**
 * Sends cart contents via email
 * @param {string} emailAddress - The recipient email address
 * @param {string} cartId - The cart/context token ID
 * @param {string} licensePlate - The cart/context token ID
 * @param {Array} products - Array of cart products
 * @returns {Promise<Object>} Response data
 */
export async function emailCart(emailAddress, cartId, licensePlate, products) {
    const url = '/api/cart/email';

    try {
        const response = await Axios.post(url, {
            email: emailAddress,
            cartId: cartId,
            licensePlate: licensePlate
        }, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * Checks if a product already exists in the cart
 * @param {string} productId - Product ID to check
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<boolean>} True if product exists in cart
 */
export async function isProductInCart({productId, shopwareUrl}) {
    try {
        const cartData = await fetchOrCreateCart({shopwareUrl});

        if (!cartData.lineItems || !Array.isArray(cartData.lineItems)) {
            return false;
        }

        // Check if any line item has the same referencedId (product ID)
        return cartData.lineItems.some(item =>
            item.referencedId === productId || item.id === productId
        );
    } catch (error) {
        return false;
    }
}

/**
 * Adds a product to the cart with duplicate prevention and improved error handling
 * @param {string} id - Product ID
 * @param {string} shopwareUrl - The Shopware API URL
 * @param {boolean} allowDuplicates - Whether to allow duplicate products (default: false)
 * @returns {Promise<Object>} Cart line items or throws DuplicateProductError
 */
export async function addToCart({id, shopwareUrl, allowDuplicates = false}) {
    // Check for duplicates unless explicitly allowed
    if (!allowDuplicates) {
        const productExists = await isProductInCart({productId: id, shopwareUrl});
        if (productExists) {
            const error = new Error('Product already exists in cart');
            error.name = 'DuplicateProductError';
            error.productId = id;
            throw error;
        }
    }

    // Ensure we have a valid cart before adding items
    await ensureValidContextToken({shopwareUrl});

    const url = `${shopwareUrl}/checkout/cart/line-item`;

    try {
        const response = await Axios.post(url,
            {
                items: [
                    {
                        id: id,
                        type: "product",
                        referencedId: id,
                        quantity: 1,
                    }
                ]
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    'sw-context-token': getShopData('sw-context-token') || ''
                }
            }
        );

        if (response.data) {
            const { elements } = response.data;

            // Dispatch cart update event
            window.dispatchEvent(new CustomEvent('cart:updated'));

            return elements;
        } else {
            throw new Error('No data found');
        }

    } catch (error) {
        // Don't retry if it's a duplicate product error
        if (error.name === 'DuplicateProductError') {
            throw error;
        }

        // Try to recover from cart-related errors
        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            // Retry the add operation with new context token
            const retryResponse = await Axios.post(url,
                {
                    items: [
                        {
                            id: id,
                            type: "product",
                            referencedId: id,
                            quantity: 1,
                            stackable: false
                        }
                    ]
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'sw-access-key': accessKey,
                        'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                        'sw-context-token': getShopData('sw-context-token') || ''
                    }
                }
            );

            if (retryResponse.data) {
                const { elements } = retryResponse.data;
                // Dispatch cart update event
                window.dispatchEvent(new CustomEvent('cart:updated'));
                return elements;
            }
        }
        throw error;
    }
}

/**
 * Removes a product from the cart with improved error handling and recovery
 * @param {string} id - Line item ID to remove
 * @param {string} shopwareUrl - The Shopware API URL
 * @returns {Promise<Object>} Updated cart data
 */
export async function removeFromCart(id, shopwareUrl) {
    // Ensure we have a valid cart before removing items
    await ensureValidContextToken({shopwareUrl});

    const url = `${shopwareUrl}/checkout/cart/line-item/delete`;

    try {
        const response = await Axios.post(url, {
            ids: [id]
        }, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                'sw-context-token': getShopData('sw-context-token')
            }
        });

        if (response.data) {
            // Dispatch cart update event
            window.dispatchEvent(new CustomEvent('cart:updated'));

            return response.data;
        } else {
            throw new Error('No data found after removal');
        }

    } catch (error) {
        // Try to recover from cart-related errors
        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            // Retry the remove operation with new context token
            const retryResponse = await Axios.post(url, {
                ids: [id]
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    'sw-context-token': getShopData('sw-context-token')
                }
            });

            if (retryResponse.data) {
                // Dispatch cart update event
                window.dispatchEvent(new CustomEvent('cart:updated'));
                return retryResponse.data;
            }
        }
        throw error;
    }
}
